import React, { useCallback, useEffect, useState, useRef } from 'react';
import { CLOUDINARY_CONFIG } from '../utils/cloudinaryConfig';

// Cloudinary image at a given size. Thumbnails ask for 900px instead of the
// original multi-megabyte photo; the viewer asks for up to 1600px.
const cloudImage = (publicId, transform) =>
  `https://res.cloudinary.com/${CLOUDINARY_CONFIG.cloudName}/image/upload/${transform}/${publicId}`;
const THUMB = 'f_auto,q_auto,c_limit,w_900';
const LARGE = 'f_auto,q_auto,c_limit,w_1600,h_1600';

const getVideoUrl = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url
    .replace('/upload/', '/upload/f_mp4,vc_auto,q_auto/')
    .replace(/\.(mkv|mov|avi|webm)$/i, '.mp4');
};

// Tile sizes per orientation, picked at random so the grid looks different on
// every visit. Weighted toward the smaller sizes so big tiles stay a highlight;
// grid-flow-dense backfills any gaps the mix leaves.
const SPANS = {
  portrait: [
    'col-span-1 row-span-2 md:col-span-2 md:row-span-3',
    'col-span-1 row-span-2 md:col-span-2 md:row-span-3',
    'col-span-1 row-span-2 md:col-span-1 md:row-span-2',
    'col-span-2 row-span-3 md:col-span-2 md:row-span-4',
  ],
  landscape: [
    'col-span-1 row-span-1 md:col-span-2 md:row-span-1',
    'col-span-2 row-span-1 md:col-span-2 md:row-span-1',
    'col-span-2 row-span-1 md:col-span-3 md:row-span-2',
    'col-span-2 row-span-2 md:col-span-4 md:row-span-2',
  ],
  square: [
    'col-span-1 row-span-1 md:col-span-1 md:row-span-1',
    'col-span-1 row-span-1 md:col-span-2 md:row-span-2',
    'col-span-2 row-span-2 md:col-span-2 md:row-span-2',
  ],
};

const pick = (list) => list[Math.floor(Math.random() * list.length)];

const getGridSpan = (orientation) => pick(SPANS[orientation] || SPANS.square);

const PAGE_SIZE = 18;

// Fisher–Yates shuffle (unbiased, unlike sort(() => Math.random() - 0.5)).
const shuffle = (list) => {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export const Gallery = () => {
  // The whole archive is small (~80 items of metadata), so it's fetched once,
  // shuffled into a fresh order on every visit, and revealed a page at a time.
  const [allImages, setAllImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/gallery-images?page=1&limit=1000');
        if (!response.ok) throw new Error('Failed to load images');
        const data = await response.json();
        if (cancelled) return;
        setAllImages(shuffle(data.images || []).map((img) => ({
          ...img,
          span: getGridSpan(img.orientation)
        })));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const displayedImages = allImages.slice(0, visibleCount);
  const hasMore = visibleCount < allImages.length;

  // Intersection Observer for Mobile Autoplay
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.play().catch(() => {});
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.6 }
    );

    videoRefs.current.forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => observer.disconnect();
  }, [displayedImages]);

  const loadMore = () => setVisibleCount((n) => n + PAGE_SIZE);

  // Full-screen viewer; steps through the whole shuffled archive.
  const [openIndex, setOpenIndex] = useState(null);
  const closeViewer = useCallback(() => setOpenIndex(null), []);
  const step = useCallback(
    (dir) => setOpenIndex((i) => (i === null ? i : (i + dir + allImages.length) % allImages.length)),
    [allImages.length]
  );


  if (loading) {
    return (
      <section className="bg-custom-background py-40 px-6 md:px-24">
        <div className="max-w-7xl mx-auto text-center font-grandslang italic text-2xl text-zinc-400">
          Loading Gallery...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-custom-background py-40 px-6 md:px-24 text-center">
        <p className="text-zinc-500">Unable to load gallery. Please sync images to database.</p>
        <code className="block text-xs text-zinc-400 mt-4">npm run sync-images</code>
      </section>
    );
  }

  return (
    <section id="visuals" className="bg-custom-background py-20 md:py-40 px-4 md:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 auto-rows-[160px] md:auto-rows-[200px] gap-3 md:gap-6 grid-flow-dense">
          {/* Header Block */}
          <div className="col-span-2 md:col-span-3 md:row-span-2 flex flex-col justify-center pr-0 md:pr-8 mb-12 md:mb-0">
            <span className="eyebrow mb-4 md:mb-6 block">
              Visual Notes
            </span>
            <h2 className="text-5xl md:text-7xl font-medium font-grandslang italic leading-[0.9] md:leading-[0.8] mb-8 md:mb-10">
              Frames & <br />Atmospheres.
            </h2>
            <div className="max-w-sm border-l-2 border-zinc-100 pl-6 mb-6">
              <p className="text-zinc-600 text-base md:text-xl leading-relaxed font-light italic">
                "Moments that caught my eye and the moods I want to remember. An ongoing archive of color and light."
              </p>
            </div>
          </div>

          {/* Media Items */}
          {displayedImages.map((img, index) => (
            <button
              type="button"
              key={img.publicId || index}
              onClick={() => setOpenIndex(index)}
              aria-label={`Open ${img.caption || img.location || (img.format === 'mp4' ? 'video' : 'photo')} in viewer`}
              className={`${img.span} block w-full h-full text-left overflow-hidden bg-zinc-100 transition-all duration-1000 group rounded-sm relative`}
            >
              {img.format === 'mp4' ? (
                <video
                  ref={(el) => (videoRefs.current[index] = el)}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  src={getVideoUrl(img.url)}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={cloudImage(img.publicId, THUMB)}
                  alt={img.caption || img.location || 'Gallery photo'}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />
              )}

              {/* Format Badge */}
              {img.format === 'mp4' && (
                <div className="absolute top-3 right-3 bg-black/20 backdrop-blur-md text-white px-2 py-1 rounded-sm text-[8px] md:text-[9px] uppercase tracking-widest font-bold z-10 pointer-events-none">
                  Motion
                </div>
              )}

              {/* Hover Info */}
              {(img.location || img.caption) && (
                <div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-4 md:p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  {img.location && (
                    <p className="text-white text-[9px] md:text-[10px] font-semibold tracking-widest uppercase mb-1">
                      {img.location}
                    </p>
                  )}
                  {img.caption && (
                    <p className="text-white/90 text-xs md:text-sm italic font-grandslang leading-snug">
                      {img.caption}
                    </p>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Pagination/Load More */}
        {displayedImages.length > 0 && (
          <div className="mt-24 md:mt-32 text-center">
            {/* <p className="text-zinc-400 text-[9px] md:text-[10px] tracking-[4px] uppercase mb-8">
              {displayedImages.length} / {allImages.length} Selected Items
            </p> */}
            {hasMore && (
              <button
                onClick={loadMore}
                className="group relative px-10 py-4 md:px-12 md:py-4 border border-charcoal/10 hover:border-charcoal transition-all duration-500 disabled:opacity-50 text-[9px] md:text-[10px] uppercase tracking-[4px] font-bold overflow-hidden"
              >
                <span className="relative z-10">Explore More</span>
              </button>
            )}
          </div>
        )}
      </div>
      {openIndex !== null && allImages[openIndex] && (
        <Lightbox items={allImages} index={openIndex} onClose={closeViewer} onStep={step} />
      )}
    </section>
  );
};

// Shows the thumbnail (already loaded from the grid) straight away, then fades
// the full-size image in over it once it arrives.
const ViewerImage = ({ item }) => {
  const [loaded, setLoaded] = useState(false);
  const alt = item.caption || item.location || 'Gallery photo';
  const size = 'max-h-[calc(100svh-10rem)] max-w-full object-contain';
  return (
    <div className="grid">
      <img src={cloudImage(item.publicId, THUMB)} alt="" aria-hidden="true" className={`${size} [grid-area:1/1] w-full h-full blur-sm`} />
      <img
        src={cloudImage(item.publicId, LARGE)}
        alt={alt}
        onLoad={() => setLoaded(true)}
        className={`${size} [grid-area:1/1] transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
};

/* Full-screen viewer: arrow keys / swipe to step, Esc or click outside to close. */
const Lightbox = ({ items, index, onClose, onStep }) => {
  const item = items[index];
  const closeRef = useRef(null);
  const touchX = useRef(null);

  // Lock page scroll and move focus in; restore both on close.
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onStep(1);
      else if (e.key === 'ArrowLeft') onStep(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);

  // Warm up the neighbours so stepping feels instant.
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const next = items[(i + items.length) % items.length];
      if (next && next.format !== 'mp4') new window.Image().src = cloudImage(next.publicId, LARGE);
    });
  }, [index, items]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gallery viewer"
      className="fixed inset-0 z-[5000] flex flex-col bg-[#141212]/95 backdrop-blur-sm text-white"
      style={{ animation: 'fade-in 0.3s ease-out' }}
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) onStep(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <div className="flex items-center justify-between px-5 md:px-10 py-5" onClick={stop}>
        <p className="eyebrow eyebrow-on-dark tabular-nums">
          <span className="text-white">{String(index + 1).padStart(2, '0')}</span> / {String(items.length).padStart(2, '0')}
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="eyebrow eyebrow-on-dark hover:!text-white transition-colors duration-300"
        >
          Close ✕
        </button>
      </div>

      <div className="relative flex-1 min-h-0 flex items-center justify-center px-4 md:px-24">
        <div key={item.publicId} className="max-h-full max-w-full flex" style={{ animation: 'fade-in 0.4s ease-out' }} onClick={stop}>
          {item.format === 'mp4' ? (
            <video src={getVideoUrl(item.url)} className="max-h-[calc(100svh-10rem)] max-w-full object-contain" autoPlay muted loop playsInline controls />
          ) : (
            <ViewerImage item={item} />
          )}
        </div>

        <button
          type="button"
          aria-label="Previous"
          onClick={(e) => { stop(e); onStep(-1); }}
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/60 transition-colors duration-300"
        >
          ←
        </button>
        <button
          type="button"
          aria-label="Next"
          onClick={(e) => { stop(e); onStep(1); }}
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/60 transition-colors duration-300"
        >
          →
        </button>
      </div>

      <div className="px-5 md:px-10 py-5 min-h-[4.5rem] text-center" onClick={stop}>
        {item.location && <p className="eyebrow eyebrow-on-dark mb-1">{item.location}</p>}
        {item.caption && <p className="font-grandslang text-lg md:text-xl text-white/90">{item.caption}</p>}
        <p className="md:hidden mt-2 text-[11px] text-white/40">Swipe to browse</p>
      </div>
    </div>
  );
};

export default Gallery;
